import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin, Select } from 'antd'
import { Dispatch, SetStateAction, useState } from 'react'

import { useStocksServiceGetInternationalNameListEndpointKey } from 'src/api/queries'
import { GetSubstanceListResponseItem, StocksService } from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  setInternationalName?: Dispatch<
    SetStateAction<GetSubstanceListResponseItem | undefined>
  >
} & SelectProps

export const InternationalNamesSelect = ({
  setInternationalName,
  ...props
}: Props) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { t } = useLocales()

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetInternationalNameListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getInternationalNameListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        undefined,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetSubstanceListResponseItem[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data?.map((item: GetSubstanceListResponseItem) => ({
        value: item.id,
        label: `${item.name} ${item.dosage}`,
        item
      })) || []
  )

  const onChange = (value: string) => {
    if (setInternationalName) {
      const selectedOption = options?.find((opt) => opt.value === value)
      setInternationalName(selectedOption?.item)
    }

    props.onChange?.(value)
  }

  return (
    <Select
      {...props}
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      placeholder={t('medicine_register.international_name.title')}
      onClear={() => setSearch('')}
      onChange={onChange}
      loading={isFetching}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
    />
  )
}
